import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { t, THEME_DEFAULT, SECTION_KEYS, EDITOR } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ali_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---------------------------------------------------------------------------
// Immutable path helpers
// ---------------------------------------------------------------------------
const clone = (v) => (Array.isArray(v) ? [...v] : typeof v === "object" && v !== null ? { ...v } : v);

const setIn = (obj, path, value) => {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const next = clone(obj ?? (typeof head === "number" ? [] : {}));
  next[head] = setIn(next[head], rest, value);
  return next;
};

const getIn = (obj, path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

// Build the full default content document from content.js
const buildDefaults = () => ({
  theme: { ...THEME_DEFAULT },
  sectionOrder: [...SECTION_KEYS],
  en: JSON.parse(JSON.stringify(t.en)),
  ar: JSON.parse(JSON.stringify(t.ar)),
});

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("ali_lang") || "en");
  const [user, setUser] = useState(null);
  const [draft, setDraft] = useState(buildDefaults);
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const draftRef = useRef(draft);
  draftRef.current = draft;

  const tr = draft[lang];
  const theme = draft.theme;
  const sectionOrder = draft.sectionOrder || SECTION_KEYS;
  const dir = tr.dir;
  const ed = EDITOR[lang];

  // ---- language + direction ----
  useEffect(() => {
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem("ali_lang", lang);
  }, [lang, dir]);

  const toggleLang = useCallback(() => setLang((p) => (p === "en" ? "ar" : "en")), []);

  // ---- apply theme to CSS variables ----
  useEffect(() => {
    const r = document.documentElement.style;
    r.setProperty("--gold", theme.accent);
    r.setProperty("--gold-bright", theme.accentBright || theme.accent);
    r.setProperty("--ink", theme.ink);
    r.setProperty("--ink-soft", theme.inkSoft || "#5C5C5C");
    r.setProperty("--bone", theme.bg);
    r.setProperty("--sand", theme.sand || "#F0EFEA");
    r.setProperty("--ivory", theme.ivory || "#FFFFFF");
    document.body.style.backgroundColor = theme.bg;
    document.documentElement.style.fontSize = `${16 * (theme.textScale || 1)}px`;
  }, [theme]);

  // ---- load content from server ----
  const loadContent = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      const defaults = buildDefaults();
      if (data && Object.keys(data).length) {
        setDraft({
          theme: { ...defaults.theme, ...(data.theme || {}) },
          sectionOrder: data.sectionOrder || defaults.sectionOrder,
          en: { ...defaults.en, ...(data.en || {}) },
          ar: { ...defaults.ar, ...(data.ar || {}) },
        });
      }
    } catch {
      /* keep defaults */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // ---- auth ----
  const checkAuth = useCallback(async () => {
    if (!localStorage.getItem("ali_token")) return;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("ali_token");
      setUser(null);
    }
  }, []);
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ali_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    localStorage.removeItem("ali_token");
    setUser(null);
    setEditMode(false);
  }, []);

  // ---- content editing (all operations set dirty) ----
  const update = useCallback(
    (path, value) => {
      setDraft((d) => setIn(d, [lang, ...path], value));
      setDirty(true);
    },
    [lang]
  );

  const updateBoth = useCallback((path, valueEn, valueAr) => {
    setDraft((d) => {
      let next = setIn(d, ["en", ...path], valueEn);
      next = setIn(next, ["ar", ...path], valueAr);
      return next;
    });
    setDirty(true);
  }, []);

  const updateTheme = useCallback((patch) => {
    setDraft((d) => ({ ...d, theme: { ...d.theme, ...patch } }));
    setDirty(true);
  }, []);

  // list ops applied to both languages to keep arrays parallel
  const listOp = useCallback((path, fn) => {
    setDraft((d) => {
      const enArr = [...(getIn(d.en, path) || [])];
      const arArr = [...(getIn(d.ar, path) || [])];
      fn(enArr, arArr);
      let next = setIn(d, ["en", ...path], enArr);
      next = setIn(next, ["ar", ...path], arArr);
      return next;
    });
    setDirty(true);
  }, []);

  const addItem = useCallback(
    (path, templateEn, templateAr) => {
      listOp(path, (en, ar) => {
        en.push(JSON.parse(JSON.stringify(templateEn)));
        ar.push(JSON.parse(JSON.stringify(templateAr)));
      });
    },
    [listOp]
  );

  const removeItem = useCallback(
    (path, index) => {
      listOp(path, (en, ar) => {
        en.splice(index, 1);
        ar.splice(index, 1);
      });
    },
    [listOp]
  );

  const moveItem = useCallback(
    (path, index, delta) => {
      listOp(path, (en, ar) => {
        const j = index + delta;
        if (j < 0 || j >= en.length) return;
        [en[index], en[j]] = [en[j], en[index]];
        [ar[index], ar[j]] = [ar[j], ar[index]];
      });
    },
    [listOp]
  );

  const moveSection = useCallback((index, delta) => {
    setDraft((d) => {
      const order = [...(d.sectionOrder || SECTION_KEYS)];
      const j = index + delta;
      if (j < 0 || j >= order.length) return d;
      [order[index], order[j]] = [order[j], order[index]];
      return { ...d, sectionOrder: order };
    });
    setDirty(true);
  }, []);

  // ---- persistence ----
  const save = useCallback(async () => {
    setSaving(true);
    try {
      await api.put("/content", { data: draftRef.current });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, []);

  const resetContent = useCallback(async () => {
    try {
      await api.delete("/content");
    } catch {
      /* noop */
    }
    setDraft(buildDefaults());
    setDirty(false);
  }, []);

  return (
    <AppContext.Provider
      value={{
        lang,
        setLang,
        toggleLang,
        tr,
        dir,
        ed,
        theme,
        sectionOrder,
        user,
        login,
        logout,
        editMode,
        setEditMode,
        dirty,
        saving,
        loaded,
        update,
        updateBoth,
        updateTheme,
        addItem,
        removeItem,
        moveItem,
        moveSection,
        save,
        resetContent,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
